import { useState, type FormEvent } from "react";
import FormField, {
  inputClass,
  textareaClass,
  selectClass,
  submitButtonClass,
} from "@/components/base/FormField";
import { submitReaddyForm, validateForm, HONEYPOT_FIELD } from "@/lib/submitForm";

const SUBMIT_ADDR = "https://readdy.ai/api/form/damjr8d1d38lmdrdaql0";
const SUCCESS_MSG = "已收到企業培訓需求，後續將依需求、對象、形式與時間進一步確認。";
const FAIL_MSG = "目前訊息暫時無法送出，請稍後再試，或透過 LINE 與我們聯絡。";

export default function CorporateForm() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = (fd.get(HONEYPOT_FIELD) as string)?.trim() || "";
    if (hp) {
      setStatus("success");
      return;
    }
    const errs = validateForm(
      fd,
      ["name", "company", "email", "problem", "expected_change"],
      "email"
    );
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");
    const res = await submitReaddyForm(form, SUBMIT_ADDR);
    if (res.ok) {
      setStatus("success");
      form.reset();
    } else {
      setStatus("error");
      setErrorMsg(res.message || FAIL_MSG);
    }
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="corporate-training-form" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField label="姓名" name="name" required error={errors.name}>
          <input id="name" name="name" className={inputClass} placeholder="您的姓名" />
        </FormField>
        <FormField label="公司／組織" name="company" required error={errors.company}>
          <input id="company" name="company" className={inputClass} placeholder="公司或組織名稱" />
        </FormField>
        <FormField label="職稱／部門" name="title">
          <input id="title" name="title" className={inputClass} placeholder="您的職稱或部門" />
        </FormField>
        <FormField label="Email" name="email" required error={errors.email}>
          <input
            id="email"
            name="email"
            type="email"
            className={inputClass}
            placeholder="您的 Email"
          />
        </FormField>
        <FormField label="電話" name="phone">
          <input id="phone" name="phone" className={inputClass} placeholder="聯絡電話" />
        </FormField>
        <FormField label="產業" name="industry">
          <input id="industry" name="industry" className={inputClass} placeholder="所屬產業" />
        </FormField>
        <FormField label="參與對象" name="audience">
          <input
            id="audience"
            name="audience"
            className={inputClass}
            placeholder="例如：中階主管、全體員工"
          />
        </FormField>
        <FormField label="預估人數" name="headcount">
          <input
            id="headcount"
            name="headcount"
            className={inputClass}
            placeholder="預估參與人數"
          />
        </FormField>
        <FormField label="目前主要問題" name="problem" required error={errors.problem}>
          <textarea
            id="problem"
            name="problem"
            className={textareaClass}
            maxLength={500}
            placeholder="企業目前最想改善的問題"
          />
        </FormField>
        <FormField
          label="希望看到的改變"
          name="expected_change"
          required
          error={errors.expected_change}
        >
          <textarea
            id="expected_change"
            name="expected_change"
            className={textareaClass}
            maxLength={500}
            placeholder="培訓後希望看到的具體改變"
          />
        </FormField>
        <FormField label="預計形式" name="format">
          <select id="format" name="format" className={selectClass} defaultValue="">
            <option value="" disabled>
              請選擇
            </option>
            <option value="專題演講">專題演講</option>
            <option value="工作坊">工作坊</option>
            <option value="系列培訓">系列培訓</option>
            <option value="企業客製專案">企業客製專案</option>
            <option value="尚未確定">尚未確定</option>
          </select>
        </FormField>
        <FormField label="希望時間" name="timeline">
          <input id="timeline" name="timeline" className={inputClass} placeholder="預計舉辦的時間" />
        </FormField>
        <FormField label="預算區間（選填）" name="budget">
          <select id="budget" name="budget" className={selectClass} defaultValue="">
            <option value="" disabled>
              請選擇
            </option>
            <option value="尚未確定">尚未確定</option>
            <option value="10 萬元以下">10 萬元以下</option>
            <option value="10–30 萬元">10–30 萬元</option>
            <option value="30–50 萬元">30–50 萬元</option>
            <option value="50 萬元以上">50 萬元以上</option>
          </select>
        </FormField>
        <FormField label="其他補充" name="note">
          <textarea
            id="note"
            name="note"
            className={textareaClass}
            maxLength={500}
            placeholder="其他想補充的內容（選填）"
          />
        </FormField>
      </div>

      <input
        type="text"
        name={HONEYPOT_FIELD}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-aux-field"
      />

      {status === "success" && (
        <p className="mt-5 rounded-md bg-success/10 border border-success/30 px-4 py-3 text-sm text-success">
          {SUCCESS_MSG}
        </p>
      )}
      {status === "error" && (
        <p className="mt-5 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
          {errorMsg}
        </p>
      )}

      <div className="mt-6">
        <button type="submit" disabled={status === "submitting"} className={submitButtonClass}>
          {status === "submitting" ? "送出中…" : "提交企業培訓需求"}
        </button>
      </div>

      <p className="mt-4 text-xs leading-[1.7] text-ink-soft/70">
        送出表單即表示您同意本網站依
        <a href="#" className="text-primary hover:text-gold underline">
          隱私權政策
        </a>
        使用您所提供的聯絡資料，以回覆本次詢問與提供相關服務資訊。
      </p>
    </form>
  );
}